import {
  Box,
  Button,
  CheckInput,
  DateInput,
  Icon,
  Select,
  SelectOptionType,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Col, Form as FormCore, Row } from 'reactstrap';
import { Subject, firstValueFrom } from 'rxjs';
import { ModalConfirmation } from '../../../components/modals/ModalConfirmation';
import { SectionTitle } from '../../../components/text';
import { useValidations } from '../../../hooks/useValidations';
import { complete } from '../requests';
import { Base } from '../types';

export interface FormValues {
  distinctionRank?: SelectOptionType;
  distinctionRanks?: SelectOptionType[];
  degreeGrade: string;
  financialStateChecked: boolean;
  date: string;
  registerCode?: string;
  certificateNumber?: string;
  registryBook?: string;
  exemptionCertificateCode?: string;
  snpSaberPro?: string;
}

export interface FormProps {
  studyPlanEnrollmentId: number | string;
  graduationGrade: string;
  degreeGrade?: string;
  distinctionRanks: { id: number; name: string }[];
  allowMultipleDistinctionRanks?: boolean;
  financialBlockade: boolean | null;
  disabled: boolean;
  degreeFinalNote?: string;
  registerCode?: string;
  certificateNumber?: string;
  registryBook?: string;
  snpSaberPro?: string;
  documents: Base['degreeDocuments'];
  exemptionCertificateCode?: string;
  showExemptionCertificateCode?: boolean;
  onComplete: () => void;
}

const dictPrefix = 'studentsDegreeStudent';

export default function Form({
  studyPlanEnrollmentId,
  onComplete,
  disabled: disabledProp,
  degreeFinalNote,
  documents,
  ...props
}: FormProps) {
  const confirmResponse$ = useRef(new Subject<boolean>());
  const [open, setOpen] = useState(false);
  const allowMultiple = props.allowMultipleDistinctionRanks === true;
  const options = useMemo(() => {
    return props.distinctionRanks.map<SelectOptionType>((d) => ({
      value: d.id,
      label: d.name,
    }));
  }, [props.distinctionRanks]);

  const { msgValidations, rulesPatternDecimal } = useValidations();

  const { t } = useTranslation();

  const texts = useMemo(
    () => ({
      distinctionRankLabel: t(`${dictPrefix}.form.distinctionRank`),
      graduationGradeLabel: t(`${dictPrefix}.form.graduationGrade`),
      degreeGradeLabel: t(`${dictPrefix}.form.degreeGrade`),
      dateLabel: t(`${dictPrefix}.form.date`),
      registerCodeLabel: t(`${dictPrefix}.form.registerCode`),
      certificateNumberLabel: t(`${dictPrefix}.form.certificateNumber`),
      snpSaberProLabel: t(`${dictPrefix}.form.snpSaberPro`),
      registryBookLabel: t(`${dictPrefix}.form.registryBook`),
      exemptionCertificateCodeLabel: t(
        `${dictPrefix}.form.exemptionCertificateCode`,
      ),
      button: t(`${dictPrefix}.form.action`),
      financialStatus: {
        description: (
          <Trans i18nKey={`${dictPrefix}.financialStatus.description`} />
        ),
        label: t(`${dictPrefix}.financialStatus.label`),
      },
      confirm: {
        title: t(`${dictPrefix}.confirm.title`),
        body: t(`${dictPrefix}.confirm.body`),
      },
      error: t(`${dictPrefix}.form.error`),
      success: t(`${dictPrefix}.form.success`),
      degreeGradeTooltip: t(`${dictPrefix}.form.degreeGradeTooltip`),
      exemptionCertificateCodeTooltip: t(
        `${dictPrefix}.form.exemptionCertificateCodeTooltip`,
      ),
    }),
    [t],
  );

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      degreeGrade: degreeFinalNote ?? '',
      financialStateChecked:
        props.financialBlockade === false ? true : undefined,
      date: '',
      registerCode: '',
      certificateNumber: '',
      registryBook: '',
      snpSaberPro: '',
    },
  });

  const { control, handleSubmit, formState, watch } = methods;

  const financialStateChecked = watch('financialStateChecked');

  const disabled =
    disabledProp ||
    !financialStateChecked ||
    documents.filter((document) => document.status !== 'approved').length > 0;

  const requestConfirmation = useCallback(async () => {
    setOpen(true);
    const confirmResponse = await firstValueFrom(confirmResponse$.current);
    setOpen(false);
    return confirmResponse;
  }, []);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const confirmResponse = await requestConfirmation();
      if (!confirmResponse) {
        return;
      }

      const distinctionRankIds = allowMultiple
        ? (values.distinctionRanks ?? []).map((o) => Number(o.value))
        : values.distinctionRank
        ? [Number(values.distinctionRank.value)]
        : [];

      const result = await complete(studyPlanEnrollmentId, {
        distinctionRankIds,
        degreeGrade: degreeFinalNote ? degreeFinalNote : values.degreeGrade,
        date: values.date,
        registerCode: values.registerCode?.trim() || undefined,
        certificateNumber: values.certificateNumber?.trim() || undefined,
        registryBook: values.registryBook?.trim() || undefined,
        exemptionCertificateCode:
          values.exemptionCertificateCode?.trim() || undefined,
        snpSaberPro: values.snpSaberPro?.trim() || undefined,
      });

      if (result.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.error,
        });
      } else {
        addToast({
          icon: 'success',
          color: 'success',
          text: texts.success,
        });
        onComplete();
      }
    },
    [
      studyPlanEnrollmentId,
      requestConfirmation,
      texts,
      onComplete,
      degreeFinalNote,
      allowMultiple,
    ],
  );

  return (
    <>
      <FormCore onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4 mt-4">
          {props.financialBlockade === null && (
            <Box
              body={
                <Row>
                  <Col className="align-self-center">
                    {texts.financialStatus.description}
                  </Col>
                  <Col xs="auto" className="align-self-center">
                    <div style={{ top: 10, position: 'relative' }}>
                      <CheckInput
                        name="financialStateChecked"
                        label={texts.financialStatus.label}
                        control={control}
                        rules={{
                          validate: (v) => v === true,
                        }}
                        disabled={disabledProp}
                      />
                    </div>
                  </Col>
                </Row>
              }
              color="secondary"
              title=""
              variant="standard"
              fullwidth
            />
          )}
          {props.financialBlockade === true && (
            <p className="text-dark d-flex align-items-center">
              <Icon className="mr-2" name="error" color="danger" size="15px" />
              {t(`${dictPrefix}.financialStateChecked.hasBlockade`)}
            </p>
          )}
          {props.financialBlockade === false && (
            <p className="text-dark d-flex align-items-center">
              <Icon
                className="mr-2"
                name="success"
                color="success"
                size="15px"
              />
              {t(`${dictPrefix}.financialStateChecked.hasntBlockade`)}
            </p>
          )}
        </div>
        <SectionTitle text={t(`${dictPrefix}.form.sectionTitle`)} />
        <Row className="mt-2">
          <Col className="pb-3" xs={12} xl={6}>
            {allowMultiple ? (
              <Select
                name="distinctionRanks"
                label={texts.distinctionRankLabel}
                options={options}
                control={control}
                disabled={disabled}
                isMulti
                rules={{
                  validate: (v) =>
                    (Array.isArray(v) && v.length > 0) ||
                    msgValidations.required,
                }}
              />
            ) : (
              <Select
                name="distinctionRank"
                label={texts.distinctionRankLabel}
                options={options}
                control={control}
                disabled={disabled}
                rules={{
                  required: msgValidations.required,
                }}
              />
            )}
          </Col>
          <Col className="pb-3" xs={12} xl={3}>
            <TextInput
              name="graduationGrade"
              label={texts.graduationGradeLabel}
              value={props.graduationGrade}
              disabled
            />
          </Col>
          <Col className="pb-3" xs={12} xl={3}>
            <TextInput
              name="degreeGrade"
              label={texts.degreeGradeLabel}
              tooltip={t(`${dictPrefix}.form.degreeGradeTooltip`)}
              control={control}
              disabled={disabled || degreeFinalNote !== null}
              rules={{
                required: msgValidations.required,
                pattern: rulesPatternDecimal,
              }}
            />
          </Col>
          <Col className="pb-3" xs={12} xl={3}>
            <DateInput
              name="date"
              label={texts.dateLabel}
              control={control}
              disabled={disabled}
              rules={{
                required: msgValidations.required,
              }}
            />
          </Col>
          <Col className="pb-3" xs={12} xl={3}>
            <TextInput
              name="registerCode"
              label={texts.registerCodeLabel}
              value={props.registerCode}
              control={control}
              disabled={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} xl={3}>
            <TextInput
              name="certificateNumber"
              label={texts.certificateNumberLabel}
              value={props.certificateNumber}
              control={control}
              disabled={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} xl={3}>
            <TextInput
              name="snpSaberPro"
              label={texts.snpSaberProLabel}
              value={props.snpSaberPro}
              control={control}
              disabled={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} xl={3}>
            <TextInput
              name="registryBook"
              label={texts.registryBookLabel}
              value={props.registryBook}
              control={control}
              disabled={disabled}
            />
          </Col>
          {props.showExemptionCertificateCode !== false && (
            <Col className="pb-3" xs={12} xl={3}>
              <TextInput
                name="exemptionCertificateCode"
                tooltip={t(
                  `${dictPrefix}.form.exemptionCertificateCodeTooltip`,
                )}
                label={texts.exemptionCertificateCodeLabel}
                value={props.exemptionCertificateCode}
                control={control}
                disabled={disabled}
              />
            </Col>
          )}
        </Row>
        <Row className="justify-content-end">
          <Col xs={12} lg={4}>
            <Button
              type="submit"
              text={texts.button}
              fullwidth
              disabled={disabled}
              loading={formState.isSubmitting}
            />
          </Col>
        </Row>
      </FormCore>
      <ModalConfirmation
        icon="information"
        onConfirm={(result) => confirmResponse$.current.next(result)}
        title={texts.confirm.title}
        subtitle={texts.confirm.body}
        open={open}
      />
    </>
  );
}
